import React, { useEffect, useMemo, useState } from 'react';
import ArrowForward from '@mui/icons-material/ArrowForwardIos';
import { useSnackbar } from 'material-ui-snackbar-provider';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { Radio, Tooltip } from '@mui/material';

import {
  refetchFeeds,
  updateFeedToMove,
  updateFeedTransferMode,
  updateFeedsToMove,
  updateShowMoveFeedModal,
} from '../../../store/videoFeedsList/videoFeedsListSlice';
import { ReactComponent as PurpleFolderIcon } from '../../../assets/icons/folder-purple-new.svg';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { ReactComponent as MovieIcon } from '../../../assets/icons/movieCollection.svg';
import { ReactComponent as TimelineIcon } from '../../../assets/icons/timeline.svg';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { findSearchValue } from '../../../helpers';
import { FOLDER_TYPE, PATHS, VIDEOFEED, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums';
import styles from './MoveVideoFeedModal.module.scss';
import { getAllFolders, getFolderById, getShallowFolder, patchCopyVideofeed, patchMoveVideofeeds } from '../../../api';
import { SortOptions, SortType } from '../../../enums/SORT';
import Modal from '../../modules/Modal';
import { beginMoveUndo } from '../../../helpers/moveUndo';

const shouldMoveAsReference = (item: VIDEOFEED) =>
  Boolean((item as any).move_as_reference) ||
  (item.parent_videofeed_id !== null &&
    item.parent_videofeed_id !== undefined);

const formatUpdatedAt = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isComposerFeed = (feed: VIDEOFEED) =>
  feed.type === VIDEOFEED_TYPE.COMPOSER ||
  feed.type === VIDEOFEED_TYPE.MY_COMPOSER;

const getFeedKindLabel = (feed: VIDEOFEED) =>
  isComposerFeed(feed) ? 'Online composer' : 'Movie collection';

const isCollectionLikeFeed = (feed: VIDEOFEED) =>
  Boolean(feed?.is_collection_root) ||
  (!feed?.parent_videofeed_id && Number(feed?.movie_collection_id || 0) > 0) ||
  Boolean(feed?.has_reference_timelines) ||
  Boolean(feed?.reference_timelines?.length);

const getFeedMoveTitle = (feed: VIDEOFEED) =>
  !shouldMoveAsReference(feed) && isCollectionLikeFeed(feed)
    ? feed.collection_name || feed.collection_title || feed.title
    : feed.title;

const buildMovedVideofeedUrl = (
  feed: VIDEOFEED,
  folderId: string,
  target: 'my_folders' | 'shared_with_me',
) => {
  const clipId = feed?.videos?.[0]?.id || 0;
  const parentPage =
    target === 'shared_with_me' ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label;

  switch (feed.type) {
    case VIDEOFEED_TYPE.COMPOSER:
      return `${PATHS.COMPOSER_PAGE.path}?feedId=${feed.id}&folderId=${folderId}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.COMPOSER}${
        target === 'shared_with_me' ? '&shared=true' : ''
      }`;
    case VIDEOFEED_TYPE.MY_COMPOSER:
      return `${PATHS.COMPOSER_PAGE.path}?feedId=${feed.id}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${
        target === 'shared_with_me' ? '&sharedComposer=true' : ''
      }`;
    default:
      return `${PATHS.TIMELINE.path}?feedId=${feed.id}&folderId=${folderId}&fileside=true&feed=0&clipId=${clipId}&clip=0&parentPage=${parentPage}&pageType=${VIDEOPAGE_TYPE.TIMELINE}${
        target === 'shared_with_me' ? '&shared=true' : ''
      }`;
  }
};

const MoveVideoFeedModal = () => {
  const sourceFolderId = findSearchValue('folderId') as string;
  const folderTrail = findSearchValue('folderTrail');
  const parentPage = findSearchValue('parentPage');
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const { showMoveFeedModal, feedToMove, feedsToMove, feedTransferMode } = useAppSelector(
    (state) => state.videoFeedsListStore,
  );
  const [selectedFolder, setSelectedFolder] = useState<FOLDER_TYPE | null>(null);
  const [selectedFolderTrail, setSelectedFolderTrail] = useState<FOLDER_TYPE[]>([]);
  const [currentFolder, setCurrentFolder] = useState<FOLDER_TYPE | null>(null);
  const [browserTrail, setBrowserTrail] = useState<FOLDER_TYPE[]>([]);
  const [sourceTrail, setSourceTrail] = useState<FOLDER_TYPE[]>([]);
  const [folderOptions, setFolderOptions] = useState<FOLDER_TYPE[]>([]);
  const [openedFolder, setOpenedFolder] = useState<FOLDER_TYPE | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const sourceAncestorIds = useMemo(
    () =>
      folderTrail
        ? decodeURIComponent(folderTrail).split(',').filter(Boolean)
        : [],
    [folderTrail],
  );

  const itemsToMove = useMemo(() => {
    const rawItems = feedsToMove.length ? feedsToMove : feedToMove ? [feedToMove] : [];
    const seen = new Set<string>();

    return rawItems.filter((item) => {
      if (!item?.id || seen.has(String(item.id))) {
        return false;
      }

      seen.add(String(item.id));
      return true;
    });
  }, [feedToMove, feedsToMove]);

  const browsingFolder = browserTrail[browserTrail.length - 1] || null;
  const actionLabel = feedTransferMode === 'copy' ? 'Copy' : 'Move';
  const moveLabel =
    itemsToMove.length === 1
      ? `${actionLabel} "${getFeedMoveTitle(itemsToMove[0])}"`
      : `${actionLabel} ${itemsToMove.length} items`;
  const rootLabel = parentPage ? decodeURIComponent(parentPage) : 'My Folders';

  const closeModal = () => {
    dispatch(updateShowMoveFeedModal(false));
    dispatch(updateFeedToMove(null));
    dispatch(updateFeedsToMove([]));
    dispatch(updateFeedTransferMode('move'));
    setCurrentFolder(null);
    setBrowserTrail([]);
    setSourceTrail([]);
    setFolderOptions([]);
    setOpenedFolder(null);
    setSelectedFolder(null);
    setSelectedFolderTrail([]);
  };

  const fetchFolders = async (parentFolderId?: string) => {
    const response = await getAllFolders({
      page: 0,
      limit: 1000,
      sort: SortType.ASC,
      type: VIDEOFEED_TYPE.TIMELINE,
      sortOption: SortOptions.NAME,
      parentFolderId,
    });

    return response.data.documents as FOLDER_TYPE[];
  };

  const openBrowserLevel = async (
    nextTrail: FOLDER_TYPE[],
    nextSelectedFolder: FOLDER_TYPE | null = selectedFolder,
    nextSelectedFolderTrail: FOLDER_TYPE[] = selectedFolderTrail,
  ) => {
    const nextParentFolderId = nextTrail[nextTrail.length - 1]?.id;
    const [nextFolders, openedFolderResponse] = await Promise.all([
      fetchFolders(nextParentFolderId),
      nextParentFolderId ? getFolderById(nextParentFolderId) : Promise.resolve(null),
    ]);
    setBrowserTrail(nextTrail);
    setFolderOptions(nextFolders);
    setOpenedFolder(openedFolderResponse?.data || null);
    setSelectedFolder(nextSelectedFolder);
    setSelectedFolderTrail(nextSelectedFolderTrail);
  };

  useEffect(() => {
    let active = true;

    const initializeModal = async () => {
      if (!showMoveFeedModal || !sourceFolderId) {
        return;
      }

      setLoading(true);

      try {
        const pathIds = [...sourceAncestorIds, sourceFolderId];
        const responses = await Promise.all(
          pathIds.map((id) => getShallowFolder(id)),
        );
        const trail = responses.map((response) => response.data as FOLDER_TYPE);
        const sourceFolder = trail[trail.length - 1] || null;
        const nextFolders = await fetchFolders(sourceFolder?.id);

        if (!active) {
          return;
        }

        setCurrentFolder(sourceFolder);
        setSourceTrail(trail);
        setBrowserTrail(trail);
        setFolderOptions(nextFolders);
        setOpenedFolder(sourceFolder);
        setSelectedFolder(null);
        setSelectedFolderTrail([]);
      } catch (err: any) {
        if (!active) {
          return;
        }

        notification.showMessage(
          err?.response?.data?.message || 'Failed to load folders',
        );
        closeModal();
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    initializeModal();

    return () => {
      active = false;
    };
  }, [showMoveFeedModal, sourceFolderId, sourceAncestorIds, notification]);

  const onOpenFolder = async (folder: FOLDER_TYPE) => {
    setLoading(true);

    try {
      await openBrowserLevel([...browserTrail, folder]);
    } finally {
      setLoading(false);
    }
  };

  const onOpenAndSelectFolder = async (folder: FOLDER_TYPE) => {
    setLoading(true);

    try {
      const nextTrail = [...browserTrail, folder];
      await openBrowserLevel(nextTrail, folder, nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const onBrowserBack = async () => {
    setLoading(true);

    try {
      const nextTrail = browserTrail.slice(0, -1);
      await openBrowserLevel(nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const onBreadcrumbClick = async (index: number) => {
    setLoading(true);

    try {
      const nextTrail = index >= 0 ? browserTrail.slice(0, index + 1) : [];
      await openBrowserLevel(nextTrail);
    } finally {
      setLoading(false);
    }
  };

  const onSelectFolder = (folder: FOLDER_TYPE) => {
    setSelectedFolder(folder);
    setSelectedFolderTrail([...browserTrail, folder]);
  };

  const onSelectCurrentFolder = () => {
    if (browsingFolder && browsingFolder.id !== sourceFolderId) {
      setSelectedFolder(browsingFolder);
      setSelectedFolderTrail(browserTrail);
    }
  };

  const moveFeedRequest = async () => {
    if (!selectedFolder || !itemsToMove.length || !sourceFolderId) {
      return;
    }

    const showMoveUndo = feedTransferMode === 'move' ? beginMoveUndo() : undefined;
    try {
      setLoading(true);

      const referenceItems = itemsToMove.filter(shouldMoveAsReference);
      const regularItems = itemsToMove.filter((item) => !shouldMoveAsReference(item));

      const referenceGroups = referenceItems.reduce<Record<string, VIDEOFEED[]>>(
        (acc, item) => {
          const key = String(item.movie_collection_id || item.parent_videofeed_id || item.id);
          acc[key] = [...(acc[key] || []), item];
          return acc;
        },
        {},
      );

      if (feedTransferMode === 'copy') {
        for (const videofeed of regularItems) {
          await patchCopyVideofeed(sourceFolderId, selectedFolder.id, videofeed.id, (videofeed as any).copy_scope);
        }
        for (const group of Object.values(referenceGroups)) {
          await patchCopyVideofeed(
            sourceFolderId,
            selectedFolder.id,
            group[0].id,
            'timeline',
            group.map((item) => item.id),
          );
        }
      } else {
        const moveFeeds = (fromId: string, toId: string) => patchMoveVideofeeds(fromId, toId, [
          ...regularItems.map((videofeed) => ({
            videofeedId: videofeed.id,
            urlPath: encodeURI(buildMovedVideofeedUrl(videofeed, toId, 'my_folders')),
            sharedUrlPath: encodeURI(buildMovedVideofeedUrl(videofeed, toId, 'shared_with_me')),
          })),
          ...Object.values(referenceGroups).map((group) => ({
            videofeedId: group[0].id,
            videofeedIds: group.map((item) => item.id),
            urlPath: encodeURI(buildMovedVideofeedUrl(group[0], toId, 'my_folders')),
            sharedUrlPath: encodeURI(buildMovedVideofeedUrl(group[0], toId, 'shared_with_me')),
          })),
        ]);
        await moveFeeds(sourceFolderId, selectedFolder.id);
        showMoveUndo?.(`Moved to ${selectedFolder.name}`, () => moveFeeds(selectedFolder.id, sourceFolderId));
      }

      dispatch(refetchFeeds());
      closeModal();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(`Failed to ${feedTransferMode} items`);
      }
    } finally {
      setLoading(false);
    }
  };

  const sourcePath = [rootLabel, ...sourceTrail.map((folder) => folder.name)].filter(Boolean).join(' / ');
  const selectedDestinationPath = selectedFolder
    ? [rootLabel, ...selectedFolderTrail.map((folder) => folder.name)]
        .filter(Boolean)
        .join(' / ')
    : '';
  const primaryItem = itemsToMove[0];
  const summaryTitle =
    itemsToMove.length === 1
      ? primaryItem ? getFeedMoveTitle(primaryItem) : ''
      : itemsToMove.length > 1
        ? `${itemsToMove.length} selected items`
        : '';
  const summaryMeta =
    itemsToMove.length === 1
      ? primaryItem
        ? shouldMoveAsReference(primaryItem)
          ? `Reference timeline${
              primaryItem.collection_name || primaryItem.collection_title
                ? ` • ${
                    primaryItem.collection_name || primaryItem.collection_title
                  }`
                : ''
            }`
          : getFeedKindLabel(primaryItem)
        : ''
      : itemsToMove.length > 1
        ? `${primaryItem?.title || ''}${itemsToMove.length > 1 ? ` +${itemsToMove.length - 1} more` : ''}`
        : '';
  const selectedItemsPreview = useMemo(
    () =>
      itemsToMove.map((item) => {
        if (shouldMoveAsReference(item)) {
          return {
            id: `reference-${item.id}`,
            title: item.title,
            meta: `Reference timeline${
              item.collection_name || item.collection_title
                ? ` • ${item.collection_name || item.collection_title}`
                : ''
            }`,
            icon: 'timeline' as const,
          };
        }

        if (isComposerFeed(item)) {
          return {
            id: `composer-${item.id}`,
            title: item.title,
            meta: 'Online composer',
            icon: 'composer' as const,
          };
        }

        return {
          id: `feed-${item.id}`,
          title: item.collection_name || item.title,
          meta: isCollectionLikeFeed(item) ? 'Movie collection' : 'Videofeed',
          icon: 'movie' as const,
        };
      }),
    [itemsToMove],
  );
  const selectedItemsTooltip = (
    <div className={styles.selectionPreview}>
      {selectedItemsPreview.map((item) => (
        <div className={styles.selectionPreviewRow} key={item.id}>
          {item.icon === 'composer' ? (
            <ComposerIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewComposerIcon}`}
            />
          ) : item.icon === 'timeline' ? (
            <TimelineIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewTimelineIcon}`}
            />
          ) : (
            <MovieIcon
              className={`${styles.selectionPreviewIcon} ${styles.selectionPreviewMovieIcon}`}
            />
          )}
          <div className={styles.selectionPreviewText}>
            <div className={styles.selectionPreviewLabel}>{item.title}</div>
            <div className={styles.selectionPreviewMeta}>{item.meta}</div>
          </div>
        </div>
      ))}
    </div>
  );
  const visibleDestinationFeeds = (openedFolder?.videofeeds || []).filter(
    (feed) => String(feed?.status || '').toUpperCase() !== 'ARCHIVED',
  );
  const destinationItems = [
    ...folderOptions.map((folder) => ({ type: 'folder' as const, folder })),
    ...visibleDestinationFeeds.map((feed) => ({
      type: 'videofeed' as const,
      feed,
    })),
  ];

  return (
    <Modal open={showMoveFeedModal} onClose={closeModal} loading={loading}>
      <div className={styles.modalBody}>
        {!!itemsToMove.length && currentFolder && (
          <>
            <div className={styles.modalTitle}>{moveLabel}</div>
            <div className={styles.modalSubtitle}>
              {feedTransferMode === 'copy'
                ? 'Choose where the copied item should be created.'
                : 'Choose a new location for this item.'}
            </div>

            <div className={styles.summaryGrid}>
              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>
                  {feedTransferMode === 'copy' ? 'Copying' : 'Moving'}
                </div>
                <Tooltip
                  title={selectedItemsTooltip}
                  placement="bottom-start"
                  arrow
                  PopperProps={{
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        backgroundColor: '#4a4a4f !important',
                        background: '#4a4a4f !important',
                        border: '1px solid #5a5a61',
                        boxShadow: '0 18px 44px rgba(0, 0, 0, 0.34)',
                        borderRadius: 'var(--ui-border-radius)',
                        maxWidth: 360,
                        padding: '0 !important',
                        opacity: '1 !important',
                        filter: 'none !important',
                        backdropFilter: 'none !important',
                        WebkitBackdropFilter: 'none !important',
                        backgroundImage: 'none !important',
                      },
                      '& .MuiTooltip-arrow': {
                        color: '#4a4a4f !important',
                      },
                      '& .MuiTooltip-tooltip *': {
                        opacity: '1 !important',
                      },
                    },
                  }}
                >
                  <div className={styles.infoContent}>
                    <div className={styles.infoIconWrap}>
                      {itemsToMove.length === 1 && isComposerFeed(itemsToMove[0]) ? (
                        <ComposerIcon className={styles.summaryIcon} />
                      ) : itemsToMove.length === 1 && shouldMoveAsReference(itemsToMove[0]) ? (
                        <TimelineIcon className={`${styles.summaryIcon} ${styles.selectionPreviewTimelineIcon}`} />
                      ) : (
                        <MovieIcon className={styles.summaryIcon} />
                      )}
                    </div>
                    <div className={styles.infoText}>
                      <div className={styles.infoTitleText}>{summaryTitle}</div>
                      <div className={styles.infoMetaText}>{summaryMeta}</div>
                    </div>
                  </div>
                </Tooltip>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.infoLabel}>Current location</div>
                <div className={styles.infoContent}>
                  <div className={styles.infoIconWrap}>
                    <PurpleFolderIcon className={styles.summaryIcon} />
                  </div>
                  <div className={styles.infoText}>
                    <div className={styles.infoTitleText}>
                      {currentFolder?.name || rootLabel}
                    </div>
                    <div className={styles.infoMetaText}>{sourcePath}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.browserShell}>
              <div className={styles.browserToolbar}>
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={onBrowserBack}
                  disabled={!browserTrail.length}
                >
                  <ArrowBack />
                </button>
                <div className={styles.browserPath}>
                  <button
                    type="button"
                    className={styles.browserPathButton}
                    onClick={() => onBreadcrumbClick(-1)}
                  >
                    {rootLabel}
                  </button>
                  {browserTrail.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <span className={styles.browserPathSeparator}>/</span>
                      <button
                        type="button"
                        className={styles.browserPathButton}
                        onClick={() => onBreadcrumbClick(index)}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.currentFolderButton}
                  onClick={onSelectCurrentFolder}
                  disabled={!browsingFolder || browsingFolder.id === sourceFolderId}
                >
                  {selectedFolder?.id === browsingFolder?.id ? 'Current folder selected' : 'Select this folder'}
                </button>
              </div>

              <div className={styles.destinationBar}>
                <div className={styles.destinationHeading}>Selected destination</div>
                <div className={styles.destinationValue}>
                  {selectedFolder ? selectedDestinationPath : 'Choose a folder from the list below'}
                </div>
                <div className={styles.selectionHint}>
                  Click a row to open and select it. Use the circle to select only.
                </div>
              </div>

              <div className={styles.listShell}>
                <div className={styles.listHeader}>
                  <span>Name</span>
                  <span>Type</span>
                  <span>Updated</span>
                  <span></span>
                </div>

                <div className={styles.folderList}>
                  {!destinationItems.length && (
                    <div className={styles.emptyState}>No items found</div>
                  )}

                  {destinationItems.map((item) => {
                    if (item.type === 'folder') {
                      const { folder } = item;
                      const isSelected = selectedFolder?.id === folder.id;

                      return (
                        <div
                          className={`${styles.listRow} ${styles.folderRow} ${
                            isSelected ? styles.folderSelected : ''
                          }`}
                          key={`folder-${folder.id}`}
                          onClick={() => onOpenAndSelectFolder(folder)}
                        >
                          <div className={styles.rowMain}>
                            <Radio
                              checked={isSelected}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => onSelectFolder(folder)}
                              sx={{
                                color: 'white',
                                '&, &.Mui-checked': {
                                  color: '#39b3ea',
                                },
                              }}
                              size="small"
                            />
                            <PurpleFolderIcon className={styles.rowIcon} />
                            <div className={styles.rowTextWrap}>
                              <div className={styles.rowPrimary}>{folder.name}</div>
                              <div className={styles.rowSecondary}>
                                {folder.parent_folder_id ? 'Subfolder' : 'Folder'}
                              </div>
                            </div>
                          </div>
                          <div className={styles.rowType}>
                            {folder.parent_folder_id ? 'Subfolder' : 'Folder'}
                          </div>
                          <div className={styles.rowMeta}>{formatUpdatedAt(folder.updated_at)}</div>
                          <button
                            type="button"
                            className={styles.navigateButton}
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenFolder(folder);
                            }}
                          >
                            <ArrowForward sx={{ fontSize: '12px' }} />
                          </button>
                        </div>
                      );
                    }

                    const { feed } = item;

                    return (
                      <div className={`${styles.listRow} ${styles.disabledRow}`} key={`feed-${feed.id}`}>
                        <div className={styles.rowMain}>
                          <div className={styles.radioSpacer} />
                          {isComposerFeed(feed) ? (
                            <ComposerIcon className={styles.rowIcon} />
                          ) : (
                            <MovieIcon className={styles.rowIcon} />
                          )}
                          <div className={styles.rowTextWrap}>
                            <div className={styles.rowPrimary}>{feed.title}</div>
                            <div className={styles.rowSecondary}>Not selectable</div>
                          </div>
                        </div>
                        <div className={styles.rowType}>{getFeedKindLabel(feed)}</div>
                        <div className={styles.rowMeta}>{formatUpdatedAt(feed.updated_at)}</div>
                        <div className={styles.rowMetaMuted}>—</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={styles.footerNote}>
              {selectedFolder
                ? `${actionLabel} destination: ${selectedDestinationPath}`
                : `Select a folder to ${actionLabel.toLowerCase()} this content into.`}
            </div>

            <div className={styles.actionBtns}>
              <OutlinedButton
                kind="default"
                disabled={loading}
                onClick={closeModal}
              >
                Cancel
              </OutlinedButton>
              <ContainedButton
                onClick={moveFeedRequest}
                disabled={!selectedFolder || loading}
              >
                {actionLabel} here
              </ContainedButton>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MoveVideoFeedModal;
